import {Component, HostBinding, Input, OnInit} from '@angular/core';
import {
	AbstractControl,
	ControlValueAccessor,
	NG_VALIDATORS,
	NG_VALUE_ACCESSOR,
	ValidationErrors,
	Validator,
	Validators
} from '@angular/forms';
import {BsLocaleService} from 'ngx-bootstrap/datepicker';
import {isAfter, isBefore, isSameDay, isValid, startOfDay} from 'date-fns';
import {LocaleService} from '../../../configurations/locale/locale-service';

@Component({
	selector: 'aga-date-picker[id]',
	templateUrl: './date-picker.component.html',
	styleUrls: ['./date-picker.component.scss'],
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: DatePickerComponent,
			multi: true
		},
		{
			provide: NG_VALIDATORS,
			useExisting: DatePickerComponent,
			multi: true
		}
	]
})
export class DatePickerComponent implements OnInit, ControlValueAccessor, Validator {
	@HostBinding('class.focused') isFocused: boolean;
	@HostBinding('class.disabled') isDisabled: boolean;
	@HostBinding('class.is-empty') isEmpty = true;
	@Input() id: string;
	@Input() label: string;
	@Input() minDate: Date;
	@Input() placement: 'top' | 'bottom' | 'left' | 'right' = 'bottom';
	@Input() maxDate: Date;
	@Input() disabledDates: Date[];
	dateFormat: string = 'yyyy-MM-dd';
	_date: Date;
	isProgrammaticChange: boolean;

	onChange: any = () => {
	};

	onTouch: any = () => {
	};
	isRequired: boolean;

	constructor(public localeService: LocaleService, private bsLocaleService: BsLocaleService) {
	}

	ngOnInit(): void {
		this.bsLocaleService.use(this.localeService.getLocale());
	}

	get date() {
		return this._date;
	}

	setDateFromDateString(dateString: string): void {
		if (this.isDateValidForManualInput(dateString)) {
			this.date = startOfDay(new Date(dateString));
		} else {
			this.date = null;
		}
	}

	isDateValidForManualInput(dateString: string): boolean {
		const date = new Date(dateString);
		if (!isValid(date)) {
			return false;
		}
		let isValidWithinBoundaries = true;
		if (this.minDate && this.maxDate) {
			isValidWithinBoundaries = (isAfter(date, this.minDate) && isBefore(date, this.maxDate)) ||
				(isSameDay(date, this.minDate) || isSameDay(date, this.maxDate));
		} else {
			if (this.minDate) {
				isValidWithinBoundaries = isAfter(date, this.minDate) || isSameDay(date, this.minDate);
			}
			if (this.maxDate) {
				isValidWithinBoundaries = isBefore(date, this.maxDate) || isSameDay(date, this.maxDate);
			}
		}
		return isValidWithinBoundaries;
	}

	set date(date: Date) {
		this.isEmpty = !isValid(date);
		this._date = date;
		if (!this.isProgrammaticChange) {
			this.onTouch(date);
			this.onChange(date);
		} else {
			this.isProgrammaticChange = false;
		}
	}

	registerOnChange(fn: any): void {
		this.onChange = fn;
	}

	registerOnTouched(fn: any): void {
		this.onTouch = fn;
	}

	setDisabledState(isDisabled: boolean): void {
		this.isDisabled = isDisabled;
	}

	writeValue(date: Date): void {
		this.isProgrammaticChange = true;
		this.date = date;
	}

	updateValue(date: Date, isOpen: boolean) {
		if (isOpen) {
			this.date = date;
		}
	}

	validate(control: AbstractControl): ValidationErrors | null {
		this.isRequired = control.hasValidator(Validators.required) || control.errors?.required;
		return null;
	}
}
